<template>
    <div>
        <div class="p-grid">
            <div class="p-col-12">
                <div class="card">
                    <div class="flex justify-between items-center mb-4 p-4">
                        <h2 class="text-2xl font-bold text-gray-800">Detail Organisasi Pengadaan</h2>
                        <Button 
                            icon="pi pi-arrow-left" 
                            label="Kembali" 
                            class="p-button-secondary"
                            @click="goBack()"
                        />
                    </div>
                    
                    <div class="container px-5 py-5 mx-auto">
                        <div class="flex flex-wrap w-full mb-5 flex-col">
                                
                                <Accordion :multiple="true" :activeIndex="getActiveAccordionIndexes()">
                                    <!-- Paket Pengadaan Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.paket_pengadaan">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-box text-blue-500"></i>
                                                <span class="font-bold white-space-nowrap">Paket Pengadaan</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.nama_paket_pengadaan" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Nama Paket Pengadaan" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.nama_paket_pengadaan }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.pagu_anggaran" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Pagu Anggaran" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ formatCurrency(organisasi_pengadaan_data.paket_pengadaan.pagu_anggaran) }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.nama_pekerjaan" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Jenis Pekerjaan" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.nama_pekerjaan }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.spesifikasi_pekerjaan" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Spesifikasi Pekerjaan" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.spesifikasi_pekerjaan }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.nama_metode_pemilihan" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Metode Pemilihan" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        <Badge :value="organisasi_pengadaan_data.paket_pengadaan.nama_metode_pemilihan" 
                                                               :severity="getMetodeSeverity(organisasi_pengadaan_data.paket_pengadaan.nama_metode_pemilihan)" />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.jadwal_mulai_pemilihan_penyedia" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Jadwal Mulai Pemilihan Penyedia" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.jadwal_mulai_pemilihan_penyedia }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.jadwal_selesai_pemilihan_penyedia" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Jadwal Selesai Pemilihan Penyedia" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.jadwal_selesai_pemilihan_penyedia }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.paket_pengadaan.unit" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Unit" />
                                                    <div class="p-3 bg-gray-50 rounded border">
                                                        {{ organisasi_pengadaan_data.paket_pengadaan.nama_unit }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Organisasi Pengadaan (Pengadaan Langsung) Section -->
                                    <AccordionTab v-if="isPengadaanLangsung && hasOrganisasiPengadaanData">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-users text-green-500"></i>
                                                <span class="font-bold white-space-nowrap">Organisasi Pengadaan (Pengadaan Langsung)</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-if="organisasi_pengadaan_data.nama_ppk" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Pejabat Pembuat Komitmen (PPK)" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-blue-500"></i>
                                                        {{ organisasi_pengadaan_data.nama_ppk }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.nama_pp" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Pejabat Pengadaan (PP)" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-blue-500"></i>
                                                        {{ organisasi_pengadaan_data.nama_pp }}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div v-if="organisasi_pengadaan_data.nama_pphp" class="p-4 md:col-span-2">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Pejabat Penerima Hasil Pekerjaan (PPHP)" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-blue-500"></i>
                                                        {{ organisasi_pengadaan_data.nama_pphp }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Organisasi Pengadaan (Tender) Section -->
                                    <AccordionTab v-if="!isPengadaanLangsung && hasOrganisasiPengadaanData">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-users text-orange-500"></i>
                                                <span class="font-bold white-space-nowrap">Organisasi Pengadaan (Tender)</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-if="organisasi_pengadaan_data.nama_ppk" class="p-4 md:col-span-2">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" value="Pejabat Pembuat Komitmen (PPK)" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-blue-500"></i>
                                                        {{ organisasi_pengadaan_data.nama_ppk }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- POKJA Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.pokja && organisasi_pengadaan_data.pokja.length > 0">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-sitemap text-purple-500"></i>
                                                <span class="font-bold white-space-nowrap">POKJA ({{ organisasi_pengadaan_data.pokja.length }})</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-for="(pokja, index) in organisasi_pengadaan_data.pokja" :key="pokja.id" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" :value="`POKJA ${index + 1}`" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-purple-500"></i>
                                                        {{ pokja.user?.fullname || pokja.fullname || 'Nama tidak tersedia' }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- PPHP Tender Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.pphp_tender && organisasi_pengadaan_data.pphp_tender.length > 0">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-clipboard text-indigo-500"></i>
                                                <span class="font-bold white-space-nowrap">PPHP Tender ({{ organisasi_pengadaan_data.pphp_tender.length }})</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-for="(pphp, index) in organisasi_pengadaan_data.pphp_tender" :key="pphp.id" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" :value="`PPHP ${index + 1}`" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-indigo-500"></i>
                                                        {{ pphp.user?.fullname || pphp.fullname || 'Nama tidak tersedia' }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Tim Teknis Perencanaan Pengadaan Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.ttpp && organisasi_pengadaan_data.ttpp.length > 0">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-cog text-teal-500"></i>
                                                <span class="font-bold white-space-nowrap">Tim Teknis Perencanaan Pengadaan ({{ organisasi_pengadaan_data.ttpp.length }})</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-for="(ttpp, index) in organisasi_pengadaan_data.ttpp" :key="ttpp.id" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" :value="`TTPP ${index + 1}`" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-teal-500"></i>
                                                        {{ ttpp.user?.fullname || ttpp.fullname || 'Nama tidak tersedia' }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Tim Teknis Pelaksanaan Kontrak Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.ttpk && organisasi_pengadaan_data.ttpk.length > 0">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-wrench text-amber-500"></i>
                                                <span class="font-bold white-space-nowrap">Tim Teknis Pelaksanaan Kontrak ({{ organisasi_pengadaan_data.ttpk.length }})</span>
                                            </span>
                                        </template>
                                        
                                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div v-for="(ttpk, index) in organisasi_pengadaan_data.ttpk" :key="ttpk.id" class="p-4">
                                                <div class="flex flex-col gap-2">
                                                    <JetLabel class="font-semibold text-gray-700" :value="`TTPK ${index + 1}`" />
                                                    <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                        <i class="pi pi-user text-amber-500"></i>
                                                        {{ ttpk.user?.fullname || ttpk.fullname || 'Nama tidak tersedia' }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>

                                    <!-- Status Verifikasi Section -->
                                    <AccordionTab v-if="organisasi_pengadaan_data.last_verifikasi">
                                        <template #header>
                                            <span class="flex align-items-center gap-2 w-full">
                                                <i class="pi pi-check-circle text-green-600"></i>
                                                <span class="font-bold white-space-nowrap">Status Verifikasi</span>
                                            </span>
                                        </template>
                                        
                                        <div class="p-4">
                                            <div class="flex flex-col gap-2">
                                                <JetLabel class="font-semibold text-gray-700" value="Status Terakhir" />
                                                <div class="p-3 bg-gray-50 rounded border flex items-center gap-2">
                                                    <Badge :value="organisasi_pengadaan_data.last_verifikasi.verifikasi" 
                                                           :severity="getStatusSeverity(organisasi_pengadaan_data.last_verifikasi.verifikasi)" />
                                                </div>
                                            </div>
                                        </div>
                                    </AccordionTab>
                                </Accordion>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppLayout from "../../Layouts/AppLayout";
import JetLabel from "../../Jetstream/Label";
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import Button from "primevue/button";
import Badge from 'primevue/badge';

export default {
    name: "Show",
    layout: AppLayout,
    components: {
        JetLabel,
        Accordion,
        AccordionTab,
        Button,
        Badge,
    },
    props: {
        organisasi_pengadaan_data: Object,
    },
    computed: {
        isPengadaanLangsung() {
            return this.organisasi_pengadaan_data?.paket_pengadaan?.nama_metode_pemilihan === "Pengadaan Langsung";
        },
        hasOrganisasiPengadaanData() {
            return this.organisasi_pengadaan_data?.nama_ppk || 
                   this.organisasi_pengadaan_data?.nama_pp || 
                   this.organisasi_pengadaan_data?.nama_pphp;
        },
    },
    methods: {
        goBack() {
            this.$inertia.visit(this.route('organisasi_pengadaan.index'));
        },
        formatCurrency(amount) {
            if (!amount) return '-';
            return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        },
        getMetodeSeverity(metode) {
            switch (metode) {
                case 'Pengadaan Langsung':
                    return 'success';
                case 'Tender':
                    return 'info';
                case 'Penunjukan Langsung':
                    return 'warning';
                default:
                    return 'secondary';
            }
        },
        getStatusSeverity(status) {
            switch (status) {
                case 'Selesai':
                    return 'success';
                case 'Diajukan':
                    return 'info';
                case 'VerifikasiPA':
                    return 'warning';
                case 'Dilanjutkan':
                    return 'info';
                default:
                    return 'secondary';
            }
        },
        getActiveAccordionIndexes() {
            const indexes = [];
            let currentIndex = 0;
            
            // Paket Pengadaan
            if (this.organisasi_pengadaan_data?.paket_pengadaan) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // Organisasi Pengadaan
            if (this.hasOrganisasiPengadaanData) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // POKJA
            if (this.organisasi_pengadaan_data?.pokja?.length > 0) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // PPHP Tender
            if (this.organisasi_pengadaan_data?.pphp_tender?.length > 0) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // TTPP
            if (this.organisasi_pengadaan_data?.ttpp?.length > 0) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // TTPK
            if (this.organisasi_pengadaan_data?.ttpk?.length > 0) {
                indexes.push(currentIndex);
            }
            currentIndex++;
            
            // Status Verifikasi
            if (this.organisasi_pengadaan_data?.last_verifikasi) {
                indexes.push(currentIndex);
            }
            
            return indexes;
        },
    },
    mounted() {
        console.log("Organisasi Pengadaan Data:", this.organisasi_pengadaan_data);
    }
}
</script>

<style scoped>
.grid {
    display: grid;
}

.grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
    .md\:grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    
    .md\:col-span-2 {
        grid-column: span 2 / span 2;
    }
}

.gap-4 {
    gap: 1rem;
}

.gap-2 {
    gap: 0.5rem;
}

.gap-1 {
    gap: 0.25rem;
}
</style>
